import { useTranslation } from "react-i18next";

import { TableActionsMenu, type TableAction } from "@/components/shared/TableActionsMenu";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { PlatformTenant } from "@/services/platformTenantsService";
import { closureNoticeLabel } from "@/pages/tenants/closureNotice";
import { statusBadgeVariant } from "@/pages/tenants/tenantStatusBadge";


type TenantMobileCardProps = {
  tenant: PlatformTenant;
  logoUrl: string | null;
  displayName: string;
  actions: TableAction[];
  onCardClick: () => void;
};

export function TenantMobileCard({
  tenant,
  logoUrl,
  displayName,
  actions,
  onCardClick,
}: TenantMobileCardProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <Card
      className={cn(
        "overflow-hidden border-border p-4 text-left transition-colors duration-150",
        "hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      )}
      role="button"
      tabIndex={0}
      onClick={onCardClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onCardClick();
        }
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 gap-3">
          <Avatar className="h-10 w-10 shrink-0">
            {logoUrl ? <AvatarImage src={logoUrl} alt="" /> : null}
            <AvatarFallback className="text-xs">
              {displayName.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1 space-y-2">
            <p className="font-medium text-foreground">{displayName}</p>
            <p className="text-sm tabular-nums text-muted-foreground">{tenant.rut}</p>
            <div>
              {tenant.dgiEnv?.toLowerCase() === "prod" ? (
                <StatusBadge variant="success">{t("tenants.edit.dgiEnvProd")}</StatusBadge>
              ) : tenant.dgiEnv?.toLowerCase() === "test" ? (
                <StatusBadge variant="info">{t("tenants.edit.dgiEnvTest")}</StatusBadge>
              ) : (
                <Badge variant="secondary">{t("tenants.page.dgiEnvSystem")}</Badge>
              )}
            </div>
            {tenant.status ? (
              <StatusBadge variant={statusBadgeVariant(tenant.status)}>
                {tenant.status === "active"
                  ? t("tenants.page.statusActive")
                  : tenant.status === "onboarding"
                    ? t("tenants.page.statusOnboarding")
                    : tenant.status === "literal_e_rejected"
                      ? t(
                          "tenants.page.statusLiteralERejected",
                          "Literal E rechazado",
                        )
                      : tenant.status === "pending_approval"
                        ? t("tenants.page.statusPendingApproval")
                      : t("tenants.page.statusPendingDeletion")}
              </StatusBadge>
            ) : (
              <span className="text-sm text-muted-foreground">—</span>
            )}
            {closureNoticeLabel(tenant.closureNoticeSentAt, t)}
            <p className="text-xs text-muted-foreground">
              {t("tenants.page.accountantColumn", "Contador")}:{" "}
              {tenant.accountantName?.trim()
                ? tenant.accountantName
                : t("tenants.page.accountantAuto", "Auto")}
            </p>
            <p className="line-clamp-2 text-xs text-muted-foreground">
              {tenant.literalERejectionNote?.trim()
                ? tenant.literalERejectionNote
                : "—"}
            </p>
          </div>
        </div>
        <div
          className="shrink-0"
          data-interactive
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <TableActionsMenu actions={actions} />
        </div>
      </div>
    </Card>
  );
}
